/*
  K-beauty routine quiz · 2026 upgrade.
  Compatible with the existing Shopify theme hooks and the upgraded Liquid section.
  One scored answer = exactly +1 to one result. A neutral skip scores 0.
*/
class SkinQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.panels = Array.from(this.querySelectorAll('[data-quiz-result-panel]'));
    if (!this.steps.length || !this.panels.length) return;

    this.intro = this.querySelector('[data-quiz-intro]');
    this.stage = this.querySelector('[data-quiz-stage]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.progressLabel = this.querySelector('[data-quiz-progress-label]');
    this.segments = Array.from(this.querySelectorAll('[data-quiz-segment]'));
    this.progressTemplate = this.dataset.progressTemplate || '';
    this.tiePanel = this.querySelector('[data-quiz-tiebreak]');
    this.tieOptions = this.querySelector('[data-quiz-tiebreak-options]');
    this.miniCart = document.querySelector('mini-cart');
    this.answers = [];
    this.index = 0;
    this.tieCandidates = [];

    if (this.boundClick) this.removeEventListener('click', this.boundClick);
    this.boundClick = this.onClick.bind(this);
    this.addEventListener('click', this.boundClick);
  }

  disconnectedCallback() {
    if (this.boundClick) this.removeEventListener('click', this.boundClick);
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    if (this.onHeightEnd && this.stage) this.stage.removeEventListener('transitionend', this.onHeightEnd);
  }

  static reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onClick(event) {
    const hit = (selector) => {
      const el = event.target.closest(selector);
      return el && this.contains(el) ? el : null;
    };
    if (hit('[data-quiz-start]')) return this.beginQuiz();
    if (hit('[data-quiz-restart]')) return this.beginQuiz();
    if (hit('[data-quiz-back]')) return this.goBack();

    const tie = hit('[data-quiz-tie-option]');
    if (tie) return this.answerTie(tie);
    if (hit('[data-quiz-skip]')) return this.answerSkip();
    const option = hit('[data-quiz-option]');
    if (option) return this.answer(option);
    const add = hit('[data-quiz-add]');
    if (add) return this.addRoutine(add);
  }

  transition(mutate) {
    if (SkinQuiz.reducedMotion() || !this.stage) {
      mutate();
      return;
    }
    const stage = this.stage;
    // Cancel a previous height animation if a fast user navigates again.
    if (this.onHeightEnd) {
      stage.removeEventListener('transitionend', this.onHeightEnd);
      this.onHeightEnd = null;
    }
    clearTimeout(this.releaseTimer);
    stage.classList.remove('is-measuring');
    stage.style.height = '';
    const from = stage.getBoundingClientRect().height;
    mutate();
    stage.style.height = 'auto';
    const to = stage.getBoundingClientRect().height;
    if (Math.abs(to - from) < 1) {
      stage.style.height = '';
      return;
    }
    stage.classList.add('is-measuring');
    stage.style.height = `${from}px`;
    void stage.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (stage.isConnected) stage.style.height = `${to}px`;
    });
    this.releaseHeight(stage);
  }

  releaseHeight(stage) {
    this.onHeightEnd = (event) => {
      if (event.target !== stage || event.propertyName !== 'height') return;
      stage.style.height = '';
      stage.classList.remove('is-measuring');
      stage.removeEventListener('transitionend', this.onHeightEnd);
      this.onHeightEnd = null;
      clearTimeout(this.releaseTimer);
    };
    stage.addEventListener('transitionend', this.onHeightEnd);
    this.releaseTimer = setTimeout(() => {
      if (this.onHeightEnd) this.onHeightEnd({ target: stage, propertyName: 'height' });
    }, 900);
  }

  show(panel) {
    const all = [this.intro, ...this.steps, ...this.panels, this.tiePanel].filter(Boolean);
    all.forEach((el) => {
      el.hidden = el !== panel;
      el.classList.toggle('is-active', el === panel);
    });
  }

  focusPanel(panel, selector) {
    const target = panel && panel.querySelector(selector);
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  beginQuiz() {
    this.answers = [];
    this.tieCandidates = [];
    this.index = 0;
    if (this.progress) this.progress.hidden = false;
    this.transition(() => this.showStep());
  }

  validResultIds() {
    return this.panels.map((p) => Number.parseInt(p.dataset.quizResultPanel, 10))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  answer(option) {
    const resultId = Number(option.dataset.quizResult);
    if (!Number.isInteger(resultId) || !this.validResultIds().includes(resultId)) {
      const error = this.steps[this.index]?.querySelector('[data-quiz-step-error]');
      this.setError(error, 'Esta resposta ainda não está corretamente configurada.');
      return;
    }
    this.recordAnswer(resultId);
  }

  answerSkip() {
    // A skip is not a scored answer, and the final question has no skip button.
    if (this.index >= this.steps.length - 1) return;
    this.recordAnswer(null);
  }

  recordAnswer(resultId) {
    const error = this.steps[this.index]?.querySelector('[data-quiz-step-error]');
    this.setError(error, false);
    this.answers[this.index] = resultId;
    this.index += 1;
    this.transition(() => (this.index >= this.steps.length ? this.showResult() : this.showStep()));
  }

  goBack() {
    if (this.index === 0) return;
    this.index -= 1;
    this.answers.length = this.index;
    this.tieCandidates = [];
    this.transition(() => this.showStep());
  }

  showStep() {
    const current = this.steps[this.index];
    if (!current) return;
    this.show(current);
    const back = current.querySelector('[data-quiz-back]');
    if (back) back.hidden = this.index === 0;
    this.focusPanel(current, '.skin-quiz__question');
    this.updateProgress(this.index + 1, this.steps.length);
  }

  resolveWinners() {
    const tally = new Map(this.validResultIds().map((id) => [id, 0]));
    this.answers.forEach((id) => {
      if (id !== null && tally.has(id)) tally.set(id, tally.get(id) + 1);
    });
    const max = Math.max(...tally.values());
    return [...tally.keys()].filter((id) => tally.get(id) === max);
  }

  showResult() {
    const candidates = this.resolveWinners();
    if (candidates.length === 1) return this.showWinningPanel(candidates[0]);
    // Never silently prefer a result merely because it appears first in Shopify.
    if (this.tiePanel && this.tieOptions) return this.showTieBreaker(candidates);
    // Defensive fallback if the Liquid tie-break panel was accidentally removed.
    const recentChoice = [...this.answers].reverse().find((id) => candidates.includes(id));
    return this.showWinningPanel(recentChoice || candidates[0]);
  }

  showTieBreaker(candidates) {
    this.tieCandidates = candidates;
    this.tieOptions.replaceChildren();
    candidates.forEach((id, index) => {
      const source = this.panels.find((p) => Number(p.dataset.quizResultPanel) === id);
      const name = source?.dataset.quizResultName || `Rotina ${id}`;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'skin-quiz__option';
      button.dataset.quizTieOption = String(id);
      const number = document.createElement('span');
      number.className = 'skin-quiz__option-key';
      number.setAttribute('aria-hidden', 'true');
      number.textContent = String(index + 1);
      const label = document.createElement('span');
      label.className = 'skin-quiz__option-label';
      label.textContent = name;
      button.append(number, label);
      this.tieOptions.appendChild(button);
    });
    this.show(this.tiePanel);
    this.focusPanel(this.tiePanel, '.skin-quiz__question');
    this.updateProgress(this.steps.length, this.steps.length);
  }

  answerTie(button) {
    const chosen = Number(button.dataset.quizTieOption);
    if (!this.tieCandidates.includes(chosen)) return;
    // Tie-break selection is one additional +1 vote for a tied candidate.
    this.transition(() => this.showWinningPanel(chosen));
  }

  showWinningPanel(id) {
    const panel = this.panels.find((p) => Number(p.dataset.quizResultPanel) === id);
    if (!panel) return;
    this.show(panel);
    this.focusPanel(panel, '.skin-quiz__result-heading');
    this.updateProgress(this.steps.length, this.steps.length);
  }

  updateProgress(current, total) {
    this.segments.forEach((segment, i) => segment.classList.toggle('is-filled', i < current));
    if (this.progressLabel && this.progressTemplate) {
      this.progressLabel.textContent = this.progressTemplate
        .replace('[current]', current).replace('[total]', total);
    }
  }

  addRoutine(button) {
    if (button.disabled || button.classList.contains('loading')) return;
    const ids = [...new Set((button.dataset.variantIds || '')
      .split(',').map((id) => id.trim()).filter((id) => /^\d+$/.test(id)))];
    const errorTarget = button.closest('[data-quiz-result-panel]')?.querySelector(
      button.closest('.skin-quiz__spf') ? '.skin-quiz__spf [data-quiz-error]' : ':scope > [data-quiz-error]'
    );
    this.setError(errorTarget, false);
    if (!ids.length) {
      this.setError(errorTarget, 'Não existem produtos disponíveis para adicionar.');
      return;
    }

    const items = { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) };
    // Use Shopify's JSON cart endpoint for both drawer and cart-page modes.
    // Posting an `items` array through a traditional form helper is unreliable.
    const cartPageMode = document.body.classList.contains('template-cart') ||
      !window.theme?.shopSettings?.cartDrawer;

    button.disabled = true;
    button.classList.add('loading');
    const sections = this.miniCart?.getSectionsToRender?.().map((s) => s.id) || [];
    const body = JSON.stringify({ ...items, sections, sections_url: window.location.pathname });
    const route = window.theme?.routes?.cart_add_url || '/cart/add.js';
    const config = typeof window.fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    fetch(route, { ...config, body })
      .then((response) => response.json().then((payload) => ({ ok: response.ok, payload })))
      .then(({ ok, payload }) => {
        if (!ok || payload.status) {
          this.setError(errorTarget, payload.description || 'Não foi possível adicionar ao carrinho.');
          return;
        }
        if (cartPageMode) {
          window.location.assign(window.theme?.routes?.cart_url || '/cart');
          return;
        }
        this.miniCart?.renderContents?.(payload);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: payload } }));
      })
      .catch((error) => {
        console.error('Skin quiz: cart add failed', error);
        this.setError(errorTarget, 'Ocorreu um erro ao adicionar os produtos. Tente novamente.');
      })
      .finally(() => {
        button.classList.remove('loading');
        button.disabled = false;
      });
  }

  setError(target, message) {
    if (!target) {
      if (message) console.warn(message);
      return;
    }
    target.textContent = message || '';
    target.hidden = !message;
  }
}

if (!customElements.get('skin-quiz')) {
  customElements.define('skin-quiz', SkinQuiz);
}
